import { RadioGroup as RadioPrimitive } from 'radix-ui'
import { useId, type ReactNode } from 'react'
import { cn } from '@/utils/cn'

export interface PickerOption {
  value: string
  label: string
  disabled?: boolean
  badges?: ReactNode
  description?: ReactNode
}

interface ResourcePickerProps {
  value: string
  onValueChange: (value: string) => void
  options: PickerOption[]
  id?: string
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

/**
 * A scrollable single-choice list (radio group) whose options can carry badges — used to pick a
 * teacher or room with free / busy state. Busy options stay visible but disabled.
 */
export function ResourcePicker({ value, onValueChange, options, id, ...aria }: ResourcePickerProps) {
  const base = useId()
  return (
    <RadioPrimitive.Root
      id={id}
      value={value}
      onValueChange={onValueChange}
      className={cn(
        'max-h-56 scrollbar-thin overflow-y-auto rounded-control border border-line bg-surface p-1',
        aria['aria-invalid'] && 'border-danger-500',
      )}
      {...aria}
    >
      {options.map((o) => {
        const itemId = `${base}-${o.value}`
        return (
          <label
            key={o.value}
            htmlFor={itemId}
            className={cn(
              'flex items-center gap-2.5 rounded-xs px-2.5 py-2 text-sm transition-colors has-[[data-state=checked]]:bg-brand-50',
              o.disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-surface-sunken',
            )}
          >
            <RadioPrimitive.Item
              id={itemId}
              value={o.value}
              disabled={o.disabled}
              className="grid size-4 shrink-0 place-items-center rounded-full border border-line-strong bg-surface disabled:bg-surface-sunken data-[state=checked]:border-brand-600"
            >
              <RadioPrimitive.Indicator className="size-2 rounded-full bg-brand-600" />
            </RadioPrimitive.Item>
            <span className="min-w-0 flex-1">
              <span className={cn('block truncate font-medium', o.disabled ? 'text-muted' : 'text-ink')}>
                {o.label}
              </span>
              {o.description ? (
                <span className="block truncate text-xs text-muted">{o.description}</span>
              ) : null}
            </span>
            {o.badges ? <span className="flex shrink-0 flex-wrap justify-end gap-1">{o.badges}</span> : null}
          </label>
        )
      })}
    </RadioPrimitive.Root>
  )
}
